import {
  PunchStatus,
  PunchType,
  attributePunchDays,
  dayKeyInTimeZone,
  pairPunches,
  type UnpairedReason,
} from '@construction/shared';
import type { Punch } from '@/api/endpoints';

/**
 * 一段班次画成什么样（`SessionLine`）：
 * 正常绿实线、待复核橙线、在岗点线、缺卡红虚线、过长红实线。
 */
export type SessionStatus = 'ok' | 'pending' | 'open' | 'missingStart' | 'missingEnd' | 'tooLong';

export interface DaySession {
  key: string;
  start: Punch | null;
  end: Punch | null;
  status: SessionStatus;
  /** 起止都在时的时长（分钟）。待复核的也算出来给人看，但不进当天合计 */
  minutes: number | null;
  /** 下班跨到了第二天（夜班），画「+1」 */
  endsNextDay: boolean;
  /** 这一段是异常（按已通过的打卡配对算，和「异常」页签同一个口径），带处理记录 */
  anomaly: {
    punchId: string;
    reason: UnpairedReason;
    resolution: NonNullable<Punch['anomalyResolution']> | null;
  } | null;
}

export interface DayRow {
  /** 工地时区的 YYYY-MM-DD */
  date: string;
  sessions: DaySession[];
  /** 当天合计：只算已通过的段，和服务端汇总一致 */
  minutes: number;
  /** 这天的原始打卡（「打卡明细」里用） */
  punches: Punch[];
}

const REASON_BY_STATUS: Partial<Record<SessionStatus, UnpairedReason>> = {
  missingEnd: 'MISSING_CLOCK_OUT',
  missingStart: 'MISSING_CLOCK_IN',
  tooLong: 'SESSION_TOO_LONG',
};

const time = (punch: Punch) => new Date(punch.occurredAt).getTime();

/**
 * 工时表展开一个人：一天一行，每段「上班 ─── 下班」连成一条（考勤页 UI 打磨）。
 *
 * 客户原话「点开每个人也是一眼就看出每天的上班打卡下班，和每天时间」
 * 「同一天分段打卡上下班两个时间最好用一些把两个时间连接对应下」。
 *
 * - **画线**用已通过 + 待复核的打卡顺序配对（不含已拒绝）：待复核的那段也看得见，只是标「待复核 · 未计入」；
 * - **合计和异常**只认已通过的（shared `pairPunches`，带 `now` 和工地最长班次）—— 和汇总行、「异常」页签对得上；
 * - 一段归到它**开始**的那一天，夜班的下班时刻标「+1」；
 * - 「打卡明细」按 shared 的 `attributePunchDays` 分：夜班次日早上那张下班卡列在夜班那天，不在次日多出一行空的；
 * - 传了 `range`（CR-066）：打卡要前后多取一个最长班次（`widenRange`），这里只出区间里的日子 ——
 *   区间最后一晚的班照样配得上下班卡，区间第一天不画前一晚开始的班、也不报「缺上班卡」。
 */
export function buildDays(
  punches: Punch[],
  timezone: string,
  maxShiftMinutes: number,
  now: Date = new Date(),
  range?: { from: string; to: string },
): DayRow[] {
  const dayKey = (at: string | Date) => dayKeyInTimeZone(new Date(at), timezone);
  const rows = punches.filter((p) => p.status !== PunchStatus.REJECTED).sort((a, b) => time(a) - time(b));
  const likes = rows.map((p) => ({
    id: p.id,
    type: p.type as PunchType,
    occurredAt: new Date(p.occurredAt),
    status: p.status as PunchStatus,
  }));

  const approved = pairPunches(likes, { now, maxSessionMinutes: maxShiftMinutes });
  const anomalyByPunch = new Map(approved.anomalies.map((a) => [a.punchId, a]));
  const punchDays = attributePunchDays(likes, dayKey);
  const inRange = (date: string) => !range || (date >= range.from && date <= range.to);

  const days = new Map<string, DayRow>();
  const ensure = (date: string) => {
    const found = days.get(date) ?? { date, sessions: [], minutes: 0, punches: [] };
    days.set(date, found);
    return found;
  };

  for (const punch of rows) {
    const date = punchDays.get(punch.id) ?? dayKey(punch.occurredAt);
    if (inRange(date)) ensure(date).punches.push(punch);
  }
  for (const session of approved.sessions) {
    const date = dayKey(session.start);
    if (inRange(date)) ensure(date).minutes += session.minutes;
  }

  const push = (start: Punch | null, end: Punch | null, last: boolean) => {
    let status: SessionStatus;
    let minutes: number | null = null;
    if (start && end) {
      minutes = (time(end) - time(start)) / 60_000;
      if (start.status === PunchStatus.PENDING_REVIEW || end.status === PunchStatus.PENDING_REVIEW) status = 'pending';
      else if (minutes > maxShiftMinutes) status = 'tooLong';
      else status = 'ok';
    } else if (start) {
      // 只有最后那张上班卡才可能是「在岗」：后面又打了上班卡的，前一张一定是漏打了下班
      const elapsed = (now.getTime() - time(start)) / 60_000;
      status = last && elapsed <= maxShiftMinutes ? 'open' : 'missingEnd';
    } else {
      status = 'missingStart';
    }

    const reason = REASON_BY_STATUS[status];
    const culprit = status === 'missingStart' ? end : start;
    const found = reason && culprit ? anomalyByPunch.get(culprit.id) : undefined;
    const anchor = start ?? end;
    if (!anchor) return;
    const date = dayKey(anchor.occurredAt);
    if (!inRange(date)) return;

    ensure(date).sessions.push({
      key: `${start?.id ?? '-'}:${end?.id ?? '-'}`,
      start,
      end,
      status,
      minutes: minutes === null ? null : Math.round(minutes),
      endsNextDay: Boolean(start && end && dayKey(start.occurredAt) !== dayKey(end.occurredAt)),
      anomaly: found
        ? { punchId: found.punchId, reason: found.reason, resolution: culprit?.anomalyResolution ?? null }
        : null,
    });
  };

  let pendingIn: Punch | null = null;
  for (const punch of rows) {
    if (punch.type === PunchType.IN) {
      if (pendingIn) push(pendingIn, null, false);
      pendingIn = punch;
    } else if (pendingIn) {
      push(pendingIn, punch, false);
      pendingIn = null;
    } else {
      push(null, punch, false);
    }
  }
  if (pendingIn) push(pendingIn, null, true);

  for (const day of days.values()) {
    day.sessions.sort((a, b) => time((a.start ?? a.end)!) - time((b.start ?? b.end)!));
  }

  // 最近的一天在最上面
  return [...days.values()].sort((a, b) => (a.date < b.date ? 1 : -1));
}
